import React, { useContext, useState } from "react";
import { styled } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "react-router-dom";
import logo from "./nav-logo.svg";
import TryButton from "../Onboarding/TryButton";
import { OverviewContext } from "../../store/OverviewContext";

/**
 * PM5 T1 (chrome, batch "header") — converted from MUI v4 + `makeStyles` to
 * MUI v5+ (`@mui/material`) + `styled`/`sx`.
 * See docs/mui-conversion-guide.md; acceptance bar is visual parity.
 *
 * NOT PORTED — five `useStyles` keys that no element in this file ever
 * referenced: `title`, `search`, `searchIcon`, `inputRoot`, `inputInput`
 * (left over from a search box that lives in `Search/` now). They emitted a
 * stylesheet rule but were never applied to anything, so dropping them is
 * provably zero-visual-change. `search` was the file's only consumer of v4's
 * `fade()`, which is why no `alpha()` import replaces it.
 */

/* ---------------------------------------------------------------------- *
 * styled() — host elements that carry a fixed, reused style
 * ---------------------------------------------------------------------- */

// was: useStyles().grow — the flex spacer <div>s
const Grow = styled("div")({
  flexGrow: 1,
});

// was: useStyles().logo
const Logo = styled("img")(({ theme }) => ({
  width: "130px",
  height: "auto",
  padding: "10px 0",
  [theme.breakpoints.up("sm")]: {
    // marginLeft: theme.spacing(2)
  },
}));

// was: useStyles().sectionDesktop / .sectionMobile — plain <div>s that toggle
// with `display`, NOT with v4's <Hidden>. Both subtrees therefore stay mounted
// at every width, exactly as they do today; converting them to `useMediaQuery`
// would start unmounting them and is a behaviour change, not a port.
const SectionDesktop = styled("div")(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("sm")]: {
    display: "flex",
  },
}));

const SectionMobile = styled("div")(({ theme }) => ({
  display: "flex",
  [theme.breakpoints.up("sm")]: {
    display: "none",
  },
}));

// was: useStyles().mobileNavListItem — applied to a react-router <Link>, which
// has no `sx` prop, so it is a styled() wrapper rather than an sx object.
const MobileNavListItemLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.lightgray,
  textDecoration: "none",
  fontSize: "1.6rem",
  fontWeight: 600,
  "&:hover": {
    color: theme.palette.background.ffGreen,
  },
}));

// was: useStyles().mobileNavListItemSub — same rule on a <Link> and on a bare
// <a>; one style function, two hosts.
const mobileNavListItemSubStyles = (theme) => ({
  color: theme.palette.common.lightgray,
  textDecoration: "none",
  fontWeight: 400,
  "&:hover": {
    color: theme.palette.background.ffGreen,
  },
});

const MobileNavListItemSubLink = styled(Link)(({ theme }) =>
  mobileNavListItemSubStyles(theme),
);

const MobileNavListItemSubAnchor = styled("a")(({ theme }) =>
  mobileNavListItemSubStyles(theme),
);

/* ---------------------------------------------------------------------- *
 * sx — one-off / repeated styles on MUI components
 * ---------------------------------------------------------------------- */

// was: useStyles().externalHeader
const externalHeaderSx = {
  backgroundColor: "background.ffNavy",
};

// was: useStyles().mobileNavIcon
const mobileNavIconSx = {
  color: "common.white",
  fontSize: "2rem",
};

// was: useStyles().signInBut
// The v4 rule pins `color` itself, so this Button is not exposed to the v5+
// "Button default colour" delta (guide §2) — the label stays #fff either way.
const signInButSx = {
  border: "2px solid #fff",
  color: "#fff",
  transition: "none",
  "&:hover": {
    border: "2px solid #99ca3c",
    color: "#99ca3c",
    transition: "none",
  },
};

// was: useStyles().mobileNav — applied via v4's `classes={{ paper: … }}` on the
// Drawer. v9 exposes the same element through `slotProps.paper`.
const mobileNavPaperSx = {
  width: "100%",
  backgroundColor: "background.dark",
};

// was: useStyles().mobileNavCloseIcon
const mobileNavCloseIconSx = {
  color: "common.lightgray",
  fontSize: "2rem",
};

// was: useStyles().mobileNavListDivider
const mobileNavListDividerSx = {
  height: "2px",
  marginLeft: 2,
};

export default function Header({ showTry = true, showSignIn = true }) {
  const { overview } = useContext(OverviewContext);

  // State
  const [state, setState] = useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });

  const toggleDrawer = (side, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setState({ ...state, [side]: open });
  };

  const sideList = (side) => (
    // The v4 markup read `className={classes.list}`, but `list` was never a key
    // of this file's useStyles — the prop resolved to `undefined` and no class
    // was ever applied. Dropped rather than invented.
    <div
      role="presentation"
      onClick={toggleDrawer(side, false)}
      onKeyDown={toggleDrawer(side, false)}
    >
      {/* v9's Box reads `sx` only — the v4 shorthand system props
          (`display` / `alignItems` / `p` / `m`) and the v4-system `css` prop are
          forwarded to the DOM and apply nothing. Rewritten as sx keys with the
          same values: p/m stay spacing multiples (1 = 8px) and `height: 100`
          resolves to 100px in both systems. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          p: 1,
          m: 1,
          height: 100,
        }}
      >
        <Box>
          <List>
            {showSignIn && (
              <ListItem>
                <ListItemText
                  primary={
                    <MobileNavListItemLink to="/login">
                      Log In
                    </MobileNavListItemLink>
                  }
                />
              </ListItem>
            )}
          </List>
          <Divider sx={mobileNavListDividerSx} />
          {showTry && (
            <>
              {/*
              Doors Open & Trials Open - show Free Trial
              Doors Closed & Trials Open - show Free Trial
              Doors Open & Trials Closed - show Join Now
              Doors Closed & Trials Closed - show Free Trial (notify me status)
            */}
              {overview && (
                <>
                  {overview.doors?.open && overview.doors?.trialsOpen && (
                    <>
                      <List>
                        <ListItem>
                          <ListItemText>
                            <TryButton
                              label="Try FarmFLiX"
                              name="try-farmflix"
                              style={{
                                backgroundColor: "transparent",
                                border: "none",
                                color: "#99ca3c",
                                textTransform: "capitalize",
                                padding: "0",
                                fontSize: "1.6rem",
                                fontWeight: "600",
                                marginLeft: 0,
                              }}
                            />
                          </ListItemText>
                        </ListItem>
                      </List>
                      <Divider sx={mobileNavListDividerSx} />
                      <List>
                        <ListItem>
                          <ListItemText>
                            <Link
                              to="/buy"
                              style={{
                                backgroundColor: "transparent",
                                border: "none",
                                color: "#ddd",
                                textDecoration: "none",
                                textTransform: "capitalize",
                                padding: "0",
                                fontSize: "1.6rem",
                                fontWeight: "600",
                                marginLeft: 0,
                              }}
                            >
                              Join FarmFLiX
                            </Link>
                          </ListItemText>
                        </ListItem>
                      </List>
                    </>
                  )}

                  {!overview.doors?.open && overview.doors?.trialsOpen && (
                    <>
                      <List>
                        <ListItem>
                          <ListItemText>
                            <TryButton
                              label="Try FarmFLiX"
                              name="try-farmflix"
                              style={{
                                backgroundColor: "transparent",
                                border: "none",
                                color: "#99ca3c",
                                textTransform: "capitalize",
                                padding: "0",
                                fontSize: "1.6rem",
                                fontWeight: "600",
                                marginLeft: 0,
                              }}
                            />
                          </ListItemText>
                        </ListItem>
                      </List>
                      <Divider sx={mobileNavListDividerSx} />
                    </>
                  )}

                  {overview.doors?.open && !overview.doors?.trialsOpen && (
                    <>
                      <List>
                        <ListItem>
                          <ListItemText>
                            <Link
                              to="/buy"
                              style={{
                                backgroundColor: "transparent",
                                border: "none",
                                color: "#99ca3c",
                                textDecoration: "none",
                                textTransform: "capitalize",
                                padding: "0",
                                fontSize: "1.6rem",
                                fontWeight: "600",
                                marginLeft: 0,
                              }}
                            >
                              Join FarmFLiX
                            </Link>
                          </ListItemText>
                        </ListItem>
                      </List>
                      <Divider sx={mobileNavListDividerSx} />
                    </>
                  )}

                  {!overview.doors?.open && !overview.doors?.trialsOpen && (
                    <>
                      <List>
                        <ListItem>
                          <ListItemText>
                            <TryButton
                              label="Try FarmFLiX"
                              name="try-farmflix"
                              style={{
                                backgroundColor: "transparent",
                                border: "none",
                                color: "#99ca3c",
                                textTransform: "capitalize",
                                padding: "0",
                                fontSize: "1.6rem",
                                fontWeight: "600",
                                marginLeft: 0,
                              }}
                            />
                          </ListItemText>
                        </ListItem>
                      </List>
                      <Divider sx={mobileNavListDividerSx} />
                    </>
                  )}
                </>
              )}
            </>
          )}
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <MobileNavListItemSubLink to="/about">
                    What is FarmFLiX?
                  </MobileNavListItemSubLink>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <MobileNavListItemSubAnchor
                    href="https://shop.farmflix.tv"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    The Shop
                  </MobileNavListItemSubAnchor>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <MobileNavListItemSubLink to="/help">
                    Help
                  </MobileNavListItemSubLink>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <MobileNavListItemSubLink to="/usemycode">
                    Redeem Giftcard
                  </MobileNavListItemSubLink>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <MobileNavListItemSubLink to="/contact">
                    Contact Us
                  </MobileNavListItemSubLink>
                }
              />
            </ListItem>
          </List>
        </Box>
        <Grow />
        <Box sx={{ p: 1 }}>
          <CloseIcon
            sx={mobileNavCloseIconSx}
            onClick={toggleDrawer("right", true)}
          />
        </Box>
      </Box>
    </div>
  );

  return (
    <AppBar position="static" sx={externalHeaderSx}>
      <Toolbar>
        <Link to="/">
          <Logo src={logo} alt="FarmFLiX Logo" />
        </Link>
        <Grow />
        <SectionDesktop>
          {showSignIn && (
            <Link to="/login" style={{ textDecoration: "none" }}>
              <Button variant="outlined" sx={signInButSx} size="small">
                Log In
              </Button>
            </Link>
          )}
          {showTry && (
            <TryButton
              size="small"
              label="Try Now"
              name="try-now"
              variant="text"
            />
          )}
        </SectionDesktop>
        <SectionMobile>
          <MenuIcon
            sx={mobileNavIconSx}
            onClick={toggleDrawer("right", true)}
          />
        </SectionMobile>
      </Toolbar>
      <Drawer
        variant="temporary"
        anchor="right"
        slotProps={{ paper: { sx: mobileNavPaperSx } }}
        open={state.right}
        onClose={toggleDrawer("right", false)}
      >
        {sideList("right")}
      </Drawer>
    </AppBar>
  );
}
